import { Set, Map, List } from 'immutable'
import * as actions from 'actions/feature/features'
import { set, setIn, map, filter, getIn } from 'utils/immutable'
import { compose, curry, flatten, keys, values, path } from 'ramda'

export default {
  [actions.FEATURE_SAVED]: updateFeature,
  [actions.OBSOLETE_FEATURE_SAVED]: updateFeature,
  [actions.FEATURE_CREATED]: updateFeature,
  [actions.CREATED_FEATURE_REVISION]: updateFeature,
  [actions.FEATURE_DATA_RECEIVED]: updateOriginalFeature,
  [actions.SET_SNAPSHOT_AS_CURRENT]: updateCurrentFeature,
  [actions.SET_FEATURE_AS_CURRENT]: resetFeature,
  [actions.RESET_FEATURE]: resetFeature,
  [actions.DUPLICATE_FEATURE_REQUEST]: duplicateFeatureRequest,
  [actions.DUPLICATE_FEATURE_SUCCESS]: duplicateFeature,
  [actions.GET_DONOR_FEATURE_DATA]: getDonorFeature,
  [actions.MERGE_DONOR_LOGIC]: mergeDonorFeature,
}

function resetFeature(state) {
  const originalFeature = state.get('originalFeature')
  return state.set('currentFeature', originalFeature)
}

const mergeDeepInCurrentFeatureWith = curry((key, newData, oldData) =>
  // need to set key before merge, otherwise undefined errors
  oldData.updateIn(['currentFeature', key], (arg) =>
    arg.mergeDeepWith(
      // when there is a conflict between the old data and new data
      // we ignore the new data (2nd arg here) and keep the old
      (old) => old,
      newData
    )
  )
)

function mergeDonorFeature(
  state,
  { currentFeatureId, donorLogicMap, donorFeatureId }
) {
  const donorFunctions = donorLogicMap.get('functions')
  const donorQueries = donorLogicMap.get('queries')
  const donorOutputs = donorLogicMap.get('outputs')
  const conditionsDict = state.getIn(['currentFeature', 'conditions'])
  const newState = state
    .updateIn(
      ['currentFeature', 'feature', currentFeatureId, 'queries'],
      (queries) =>
        queries.concat(
          donorQueries.reduce((accumulator, queryUri) => {
            if (conditionsDict.get(queryUri)) {
              return accumulator
            }
            return accumulator.push(Map({ id: queryUri, schema: 'queries' }))
          }, List())
        )
    )
    .updateIn(
      ['currentFeature', 'feature', currentFeatureId, 'functions'],
      (functions) => functions.concat(donorFunctions)
    )
    .updateIn(
      ['currentFeature', 'outputMaps'],
      map((outputMap) => {
        if (donorFunctions.includes(outputMap.get('fnId'))) {
          return outputMap
            .update(
              'outputs',
              filter((id) => donorOutputs.includes(id))
            )
            .update('outputQuery', (queryId) =>
              donorQueries.includes(queryId) || !!conditionsDict.get(queryId)
                ? queryId
                : null
            )
        }
        return outputMap
      })
    )
    .updateIn(['currentFeature', 'importedEntities'], (queries) =>
      queries
        .subtract(donorQueries)
        .subtract(donorFunctions)
        .subtract(donorOutputs)
    )

  const updatedQueriesCollection = newState.updateIn(
    ['currentFeature', 'feature', currentFeatureId, 'queries'],
    (queriesList) => {
      const outputMaps = newState
        .getIn(['currentFeature', 'feature', currentFeatureId, 'functions'])
        .map((fnId) => newState.getIn(['currentFeature', 'outputMaps', fnId]))

      const missingOutputQueries = outputMaps.reduce(
        (accumulator, outputMap) => {
          const queryUri = outputMap.get('outputQuery')

          if (!queryUri) return accumulator

          if (
            !queriesList.find(
              (queryMetaObj) => queryMetaObj.get('id') === queryUri
            )
          ) {
            return accumulator.push(
              Map({
                id: queryUri,
                schema: newState.getIn(['currentFeature', 'queries', queryUri])
                  ? 'queries'
                  : 'conditions',
              })
            )
          }
          return accumulator
        },
        List()
      )

      return queriesList.concat(missingOutputQueries)
    }
  )

  return updatedQueriesCollection
}

const createImportedEntitiesSet = compose(Set, flatten, map(keys), values)

const createMissingState = (key) => (state) => {
  if (!getIn(['currentFeature', key], state)) {
    return setIn(['currentFeature', key], Map({}), state)
  }
  return state
}

function getDonorFeature(state, { data: { feature: actionFeature } }) {
  const { conditions, queries, functions, outputs, outputMaps, feature } =
    actionFeature

  const mergeConditions = mergeDeepInCurrentFeatureWith(
    'conditions',
    conditions
  )

  const importedEntities = createImportedEntitiesSet(actionFeature)

  const mergeQueries = mergeDeepInCurrentFeatureWith('queries', queries)
  const mergeFunctions = mergeDeepInCurrentFeatureWith('functions', functions)
  const mergeOutputs = mergeDeepInCurrentFeatureWith('outputs', outputs)
  const mergeOutputMaps = mergeDeepInCurrentFeatureWith(
    'outputMaps',
    outputMaps
  )
  const mergeFeature = mergeDeepInCurrentFeatureWith('feature', feature)

  const completeState = compose(
    createMissingState('queries'),
    createMissingState('conditions')
  )(state)

  return compose(
    setIn(['currentFeature', 'importedEntities'], importedEntities),
    mergeConditions,
    mergeQueries,
    mergeFunctions,
    mergeOutputMaps,
    mergeOutputs,
    mergeFeature
  )(completeState)
}

function updateOriginalFeature(state, action) {
  const {
    data: { feature },
    featureId,
  } = action
  const status = path(['feature', featureId, 'status'], feature)
  const nextState = state.set('previousStatus', status)

  return setOriginalFeature(feature, nextState)
}

function updateCurrentFeature(state, { feature }) {
  return setCurrentFeature(feature, state)
}

function updateFeature(state, { data: { feature } }) {
  const transform = compose(
    setCurrentFeature(feature),
    setOriginalFeature(feature)
  )

  return transform(state)
}

// same as update but also reset notes
function duplicateFeature(state, { data: { feature } }) {
  const transform = compose(
    set('isDuplicatingFeature', false),
    setCurrentFeature(feature),
    setOriginalFeature(feature),
    resetNotes
  )

  return transform(state)
}

function duplicateFeatureRequest(state) {
  return state.set('isDuplicatingFeature', true)
}

// //////////////////////////////////////
// HELPERS

const setCurrentFeature = set('currentFeature')
const setOriginalFeature = set('originalFeature')
const resetNotes = set('notes', [])
